// Buscas das telas de Tecnologia e de Marketing (#6504). A regra de cada número está
// em src/lib/visaoGeral/tecnologia.ts.
import { useEffect, useState } from 'react';
import { supabase } from '../../src/lib/supabase';
import { APPROVED_DECISIONS } from '../../src/lib/fechamentoMensal';
import type { DemandaConcluida, MetricaDeMidia } from '../../src/lib/visaoGeral/tecnologia';

const PAGINA = 1000;
const LOTE = 200;

export async function paginar<T>(montar: () => any): Promise<T[]> {
  const todas: T[] = [];
  for (let p = 0; ; p++) {
    const { data, error } = await montar().range(p * PAGINA, p * PAGINA + PAGINA - 1);
    if (error) throw error;
    todas.push(...((data || []) as T[]));
    if (!data || data.length < PAGINA) break;
  }
  return todas;
}

export function useBusca<T>(buscar: () => Promise<T>, deps: unknown[], rotulo: string): { dados: T | null; erro: boolean } {
  const [estado, setEstado] = useState<{ dados: T | null; erro: boolean }>({ dados: null, erro: false });
  useEffect(() => {
    let vivo = true;
    setEstado({ dados: null, erro: false });
    buscar()
      .then(dados => { if (vivo) setEstado({ dados, erro: false }); })
      .catch(e => { console.error(`${rotulo}:`, e); if (vivo) setEstado({ dados: null, erro: true }); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return estado;
}

/**
 * Solicitações concluídas no período pelas pessoas indicadas, com a marca de reaberta
 * (voltou de "concluído" alguma vez no histórico de status). Rejeitada fica de fora.
 */
export const useDemandasConcluidas = (inicio: string, fim: string, pessoaIds: string[] | null) => {
  const chave = pessoaIds ? pessoaIds.join(',') : '';
  return useBusca<DemandaConcluida[]>(async () => {
    if (!chave) return [];
    const linhas = await paginar<Omit<DemandaConcluida, 'reaberta'>>(() => supabase.from('tarefas')
      .select('id, responsavel_id, prioridade, created_at, data_conclusao, vencimento, projeto_id')
      .eq('categoria', 'solicitacao')
      .eq('status', 'concluido')
      .or('solicitacao_status.is.null,solicitacao_status.neq.rejeitado')
      .in('responsavel_id', chave.split(','))
      .gte('data_conclusao', inicio)
      .lt('data_conclusao', fim)
      .order('id'));
    const reabertas = new Set<number>();
    const ids = linhas.map(l => l.id);
    for (let i = 0; i < ids.length; i += LOTE) {
      const { data, error } = await supabase.from('tarefa_status_events')
        .select('tarefa_id, to_status')
        .in('tarefa_id', ids.slice(i, i + LOTE))
        .eq('from_status', 'concluido');
      if (error) throw error;
      for (const e of (data || []) as { tarefa_id: number; to_status: string | null }[]) if (e.to_status !== 'concluido') reabertas.add(Number(e.tarefa_id));
    }
    return linhas.map(l => ({ ...l, reaberta: reabertas.has(Number(l.id)) }));
  }, [inicio, fim, chave], 'Demandas concluídas');
};

export interface DemandaNaFila {
  id: number;
  titulo: string | null;
  prioridade: string | null;
  created_at: string;
  vencimento: string | null;
  departamento_solicitacao: string | null;
}

/** Solicitações abertas, sem responsável, dos departamentos indicados (a mais antiga primeiro). */
export const useFilaSemResponsavel = (departamentos: string[]) => {
  const chave = departamentos.join(',');
  return useBusca<DemandaNaFila[]>(() => paginar<DemandaNaFila>(() => supabase.from('tarefas')
    .select('id, titulo, prioridade, created_at, vencimento, departamento_solicitacao')
    .eq('categoria', 'solicitacao')
    .in('departamento_solicitacao', chave.split(','))
    .is('responsavel_id', null)
    .not('status', 'in', '(concluido,cancelado)')
    .or('solicitacao_status.is.null,solicitacao_status.not.in.(rejeitado,concluido)')
    .order('created_at')), [chave], 'Fila sem responsável');
};

/** Cronômetro rodando da pessoa (a entrada de tempo ainda sem fim). */
export const useCronometroAberto = (pessoaId: string) => useBusca<{ tarefaId: number; titulo: string | null; desde: string } | null>(async () => {
  const { data, error } = await supabase.from('tarefa_time_entries')
    .select('tarefa_id, started_at, tarefas(titulo)')
    .eq('user_id', pessoaId)
    .is('ended_at', null)
    .order('started_at', { ascending: false })
    .limit(1);
  if (error) throw error;
  const e = (data || [])[0] as any;
  if (!e) return null;
  const tarefa = Array.isArray(e.tarefas) ? e.tarefas[0] : e.tarefas;
  return { tarefaId: Number(e.tarefa_id), titulo: tarefa?.titulo ?? null, desde: e.started_at };
}, [pessoaId], 'Cronômetro');

/** Pessoas do departamento (ativas), pelo departamento principal ou pela lista de departamentos. */
export const useEquipeDoDepartamento = (departamento: string) => useBusca<{ id: string; full_name: string | null }[]>(async () => {
  const { data, error } = await supabase.from('profiles')
    .select('id, full_name, status')
    .or(`departamento.eq.${departamento},departamentos.cs.{${departamento}}`);
  if (error) throw error;
  return ((data || []) as any[]).filter(p => (p.status ?? 'active') === 'active').map(p => ({ id: String(p.id), full_name: p.full_name }));
}, [departamento], 'Equipe do departamento');

/**
 * Métricas do Meta Ads de `desde` até `ate` (inclusive, 'AAAA-MM-DD'): por campanha e,
 * opcionalmente, por anúncio (para os criativos).
 */
export const useMidia = (desde: string, ate: string, comAnuncios: boolean) => useBusca<{ campanhas: MetricaDeMidia[]; anuncios: MetricaDeMidia[]; ultimoDia: string | null }>(async () => {
  const [campanhas, anuncios] = await Promise.all([
    paginar<MetricaDeMidia>(() => supabase.from('marketing_meta_daily_metrics')
      .select('id, date, campaign_name, spend, leads, conversations_started')
      .gte('date', desde).lte('date', ate).order('id')),
    comAnuncios
      ? paginar<MetricaDeMidia>(() => supabase.from('marketing_meta_ad_metrics')
          .select('id, date, ad_id, ad_name, campaign_name, spend, leads, conversations_started')
          .gte('date', desde).lte('date', ate).order('id'))
      : Promise.resolve([]),
  ]);
  const ultimoDia = campanhas.reduce<string | null>((m, l) => (!m || l.date > m ? l.date : m), null);
  return { campanhas, anuncios, ultimoDia };
}, [desde, ate, comAnuncios], 'Mídia');

/** Negociações de mídia paga aprovadas no período (data da decisão da análise). */
export const useContratosDeMidia = (inicio: string, fim: string) => useBusca<{ id: number; aprovadaEm: string; kwh: number }[]>(async () => {
  const linhas = await paginar<any>(() => supabase.from('negociacoes')
    .select('id, consumo_medio_kwh, analise_cadastral!analise_cadastral_negociacao_id_fkey!inner(decisao_final, decisao_final_at)')
    .eq('origem', 'Mídia paga')
    .in('analise_cadastral.decisao_final', APPROVED_DECISIONS)
    .gte('analise_cadastral.decisao_final_at', inicio)
    .lt('analise_cadastral.decisao_final_at', fim)
    .order('id'));
  return linhas.map(l => {
    const a = Array.isArray(l.analise_cadastral) ? l.analise_cadastral[0] : l.analise_cadastral;
    return { id: Number(l.id), aprovadaEm: a?.decisao_final_at ?? '', kwh: Number(l.consumo_medio_kwh) || 0 };
  });
}, [inicio, fim], 'Contratos de mídia');
